import dayjs from 'dayjs'
import type { TFunction } from 'i18next'

// Locale-reactive rendering of notification title/body.
//
// Notifications are created server-side with German `title`/`body` strings stored
// on the doc (see functions/src/.../*Notification). Here we re-derive the display
// text from the notification `type` + `details` + `contextName` via structured
// i18n keys, so the notification center follows the user's language (and live
// switching) instead of the frozen German that was written at creation time.
//
// Safety: if a `type` has no mapping yet, OR a required interpolation value is
// missing (e.g. an older doc whose `details` predates the expected shape), we
// fall back to the stored German `title`/`body`. The worst case is therefore the
// previous behaviour, never a broken "undefined" string for a German user.
//
// The `title`/`body` below are BASE structured keys. i18next appends the plural
// (`_one`/`_other`) and context (`_withMonth`/`_taxAdvisor`) suffixes at runtime
// from the `count`/`context` options. Only these base keys are validated by
// scripts/checkI18nKeys.mjs (it is plural-suffix aware), so the suffixed catalog
// entries do not need to appear as literals here.

/**
 * The notification fields this module reads. The notification center renders
 * plain Firestore documents (no Zod converter), so `details` is loosely typed.
 */
export interface NotificationLike {
  type: string
  context?: string | null
  contextName?: string | null
  details?: Record<string, unknown> | null
  title: string
  body: string
}

type InterpolationParams = Record<string, unknown>

interface MessageConfig {
  /** Base structured key; i18next appends plural/context suffixes at runtime. */
  title?: string
  body?: string
  /** i18next context suffix (e.g. `taxAdvisor`, `company`, `auto`), or undefined. */
  context?: (n: NotificationLike) => string | undefined
  /** Param names that must be present, else we fall back to the stored German. */
  required?:
    | string[]
    | ((n: NotificationLike, context: string | undefined) => string[])
  /** Interpolation values derived from the notification. */
  params?: (n: NotificationLike) => InterpolationParams
  /**
   * Escape hatch for messages whose body is assembled rather than a single key
   * (e.g. the Personio sync-issue grouped list). Returns the localized text, or
   * null to fall back to the stored German.
   */
  render?: (
    n: NotificationLike,
    t: TFunction
  ) => { title: string; body: string } | null
}

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined

const arrayLength = (value: unknown): number | undefined =>
  Array.isArray(value) ? value.length : undefined

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined

const asStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string')
    ? (value as string[])
    : undefined

/**
 * Localize a server-stored list of changed-field keys (`address`, `banking`, …)
 * via `<keyPrefix>.<field>`, falling back to the raw key like the server does.
 */
function localizedFieldLabels(
  fields: unknown,
  keyPrefix: string,
  t: TFunction
): string | null {
  const keys = asStringArray(fields)
  if (!keys || keys.length === 0) return null
  return keys
    .map((key) => t(`${keyPrefix}.${key}`, { defaultValue: key }))
    .join(', ')
}

/** Format a `YYYY-MM` into a localized "MMMM YYYY" using the active dayjs locale. */
function formatMonth(yearMonth: unknown): string | null {
  const ym = asString(yearMonth)
  if (!ym || ym === 'unsorted') return null
  const d = dayjs(ym)
  return d.isValid() ? d.format('MMMM YYYY') : null
}

/** Format a `YYYY-MM-DD` into the numeric "DD.MM.YYYY" (locale-independent). */
function formatDate(date: unknown): string | null {
  const raw = asString(date)
  if (!raw) return null
  const d = dayjs(raw)
  return d.isValid() ? d.format('DD.MM.YYYY') : null
}

/** Format a `YYYY-MM` into the numeric "MM.YYYY" (locale-independent). */
function formatMonthShort(yearMonth: unknown): string | null {
  const ym = asString(yearMonth)
  if (!ym) return null
  const d = dayjs(ym)
  return d.isValid() ? d.format('MM.YYYY') : null
}

export const NOTIFICATION_MESSAGES: Record<string, MessageConfig> = {
  onboarding_completed: {
    title: 'frontend:notifications.messages.onboardingCompleted.title',
    body: 'frontend:notifications.messages.onboardingCompleted.body',
    required: ['contextName'],
    params: (n) => ({ contextName: n.contextName }),
  },
  company_completed: {
    title: 'frontend:notifications.messages.companyCompleted.title',
    body: 'frontend:notifications.messages.companyCompleted.body',
    required: ['contextName'],
    params: (n) => ({ contextName: n.contextName }),
  },
  new_documents: {
    title: 'frontend:notifications.messages.newDocuments.title',
    body: 'frontend:notifications.messages.newDocuments.body',
    required: ['count'],
    // The same `new_documents` type is emitted for payroll documents (with a
    // `yearMonth`) and company documents (`yearMonth` always null). They use
    // different German wording, so we pick the variant via i18next context.
    // These are mutually exclusive: company docs never carry a month.
    context: (n) => {
      if (n.details?.yearMonth == null) return 'company'
      return formatMonth(n.details?.yearMonth) ? 'withMonth' : undefined
    },
    // No `?? 0`: a doc without the `documents` array yields undefined `count`,
    // which fails the `required` check and falls back to the stored German.
    params: (n) => ({
      count: arrayLength(n.details?.documents),
      month: formatMonth(n.details?.yearMonth),
    }),
  },
  new_invoice: {
    title: 'frontend:notifications.messages.newInvoice.title',
    body: 'frontend:notifications.messages.newInvoice.body',
    context: (n) => {
      if (n.details?.minimumPayslipsInvoice === true)
        return 'taxAdvisorMinimumPayslips'
      return n.context?.startsWith('tax-advisor/') ? 'taxAdvisor' : undefined
    },
    required: (_n, context) =>
      context === undefined
        ? ['month', 'invoiceNumber']
        : ['month', 'invoiceNumber', 'companyName'],
    params: (n) => ({
      month: formatMonth(n.details?.yearMonth),
      invoiceNumber: n.details?.stripeInvoiceNumber,
      companyName: n.details?.companyName,
    }),
  },
  payment_failed: {
    title: 'frontend:notifications.messages.paymentFailed.title',
    body: 'frontend:notifications.messages.paymentFailed.body',
    // `automaticFailure` is the base key; the other two variants override it.
    context: (n) => {
      const variant = asString(n.details?.variant)
      return variant && variant !== 'automaticFailure' ? variant : undefined
    },
    required: ['month', 'variant'],
    params: (n) => ({
      month: formatMonth(n.details?.yearMonth),
      variant: asString(n.details?.variant),
    }),
  },
  payment_method_missing: {
    title: 'frontend:notifications.messages.paymentMethodMissing.title',
    body: 'frontend:notifications.messages.paymentMethodMissing.body',
    required: [],
    // Employer copy is informal ("du"), tax-advisor formal ("Sie"); English
    // collapses both, so the formal variant key just mirrors the base in en.
    context: (n) =>
      n.context?.startsWith('tax-advisor/') ? 'formal' : undefined,
  },
  upcoming_payroll_date: {
    title: 'frontend:notifications.messages.upcomingPayrollDate.title',
    body: 'frontend:notifications.messages.upcomingPayrollDate.body',
    required: ['payrollDate'],
    // payrollDate is rendered raw (YYYY-MM-DD), matching the server string.
    params: (n) => ({ payrollDate: n.details?.payrollDate }),
  },
  immediate_reporting_obligation: {
    title: 'frontend:notifications.messages.immediateReportingObligation.title',
    body: 'frontend:notifications.messages.immediateReportingObligation.body',
    required: ['employeeName'],
    params: (n) => ({ employeeName: n.details?.employeeName }),
  },
  employer_immediate_reporting_obligation: {
    title:
      'frontend:notifications.messages.employerImmediateReportingObligation.title',
    body: 'frontend:notifications.messages.employerImmediateReportingObligation.body',
    required: ['count'],
    params: (n) => ({ count: arrayLength(n.details?.employees) }),
  },
  employee_immediate_reporting_obligation: {
    title:
      'frontend:notifications.messages.employeeImmediateReportingObligation.title',
    body: 'frontend:notifications.messages.employeeImmediateReportingObligation.body',
    required: [],
  },
  new_consolidated_invoice: {
    title: 'frontend:notifications.messages.newConsolidatedInvoice.title',
    body: 'frontend:notifications.messages.newConsolidatedInvoice.body',
    required: ['month', 'invoiceNumber'],
    params: (n) => ({
      month: formatMonth(n.details?.yearMonth),
      invoiceNumber: n.details?.stripeInvoiceNumber,
    }),
  },
  new_trial_settlement: {
    title: 'frontend:notifications.messages.newTrialSettlement.title',
    body: 'frontend:notifications.messages.newTrialSettlement.body',
    required: ['number', 'month'],
    params: (n) => ({
      number: n.details?.number,
      month: formatMonth(n.details?.yearMonth),
    }),
  },
  trial_settlement_response: {
    title: 'frontend:notifications.messages.trialSettlementResponse.title',
    body: 'frontend:notifications.messages.trialSettlementResponse.body',
    context: (n) => (n.details?.status === 'rejected' ? 'rejected' : undefined),
    required: ['number', 'month', 'respondedByName', 'status'],
    params: (n) => ({
      number: n.details?.number,
      month: formatMonth(n.details?.yearMonth),
      respondedByName: n.details?.respondedByName,
      status: n.details?.status,
    }),
  },
  certificate_case_submitted: {
    title: 'frontend:notifications.messages.certificateCaseSubmitted.title',
    body: 'frontend:notifications.messages.certificateCaseSubmitted.body',
    required: ['companyName', 'employeeName'],
    params: (n) => ({
      companyName: n.contextName,
      employeeName: n.details?.employeeName,
    }),
  },
  certificate_case_completed: {
    title: 'frontend:notifications.messages.certificateCaseCompleted.title',
    body: 'frontend:notifications.messages.certificateCaseCompleted.body',
    required: ['employeeName'],
    params: (n) => ({ employeeName: n.details?.employeeName }),
  },
  certificate_case_withdrawn: {
    title: 'frontend:notifications.messages.certificateCaseWithdrawn.title',
    body: 'frontend:notifications.messages.certificateCaseWithdrawn.body',
    required: ['companyName', 'employeeName'],
    params: (n) => ({
      companyName: n.contextName,
      employeeName: n.details?.employeeName,
    }),
  },
  operations_payroll_month_confirmation: {
    title:
      'frontend:notifications.messages.operationsPayrollMonthConfirmation.title',
    body: 'frontend:notifications.messages.operationsPayrollMonthConfirmation.body',
    // Manual confirmation names the employee; automatic confirmation does not.
    context: (n) =>
      n.details?.automaticPayrollConfirmation ? 'auto' : undefined,
    required: (_n, context) =>
      context === 'auto'
        ? ['month', 'companyName']
        : ['employeeName', 'month', 'companyName'],
    params: (n) => ({
      employeeName: n.details?.employeeName,
      month: formatMonthShort(n.details?.yearMonth),
      companyName: n.contextName,
    }),
  },
  faulty_documents: {
    title: 'frontend:notifications.messages.faultyDocuments.title',
    body: 'frontend:notifications.messages.faultyDocuments.body',
    required: ['count'],
    params: (n) => ({ count: arrayLength(n.details?.documents) }),
  },
  faulty_documents_reminder: {
    title: 'frontend:notifications.messages.faultyDocumentsReminder.title',
    body: 'frontend:notifications.messages.faultyDocumentsReminder.body',
    // Two independent plurals: the document count picks the context, the day
    // count drives i18next's own plural suffix.
    context: (n) =>
      arrayLength(n.details?.documents) === 1 ? 'single' : undefined,
    required: ['count', 'documentCount'],
    params: (n) => ({
      count: asNumber(n.details?.daysSinceOldest),
      documentCount: arrayLength(n.details?.documents),
    }),
  },
  automation_run_failed: {
    title: 'frontend:notifications.messages.automationRunFailed.title',
    body: 'frontend:notifications.messages.automationRunFailed.body',
    // run is the first 8 chars of the run id; yearMonth is rendered raw.
    required: ['run', 'yearMonth'],
    params: (n) => {
      const runId = n.details?.runId
      return {
        run: runId != null ? String(runId).slice(0, 8) : undefined,
        yearMonth: n.details?.yearMonth,
      }
    },
  },
  personio_job_type_discrepancy: {
    title: 'frontend:notifications.messages.personioJobTypeDiscrepancy.title',
    body: 'frontend:notifications.messages.personioJobTypeDiscrepancy.body',
    required: ['name', 'number'],
    params: (n) => ({
      name: n.details?.employeeName,
      number: n.details?.employeeNumber,
    }),
  },
  personio_sync_issue: {
    render: renderPersonioSyncIssue,
  },
  missed_first_payroll_deadline: {
    title: 'frontend:notifications.messages.missedFirstPayrollDeadline.title',
    body: 'frontend:notifications.messages.missedFirstPayrollDeadline.body',
    required: ['contextName', 'payrollDate'],
    params: (n) => ({
      contextName: n.contextName,
      clientNumber: n.details?.clientNumber ?? 0,
      payrollDate: formatDate(n.details?.payrollDate),
    }),
  },
  company_data_changed: {
    render: renderCompanyDataChanged,
  },
  employee_personal_data_changed: {
    render: renderEmployeePersonalDataChanged,
  },
  chatwoot_customer_message: {
    title: 'frontend:notifications.messages.chatwootCustomerMessage.title',
    body: 'frontend:notifications.messages.chatwootCustomerMessage.body',
  },
  leave_request_submitted: {
    render: renderLeaveRequestSubmitted,
  },
  leave_request_decided: {
    render: renderLeaveRequestDecided,
  },
  sick_report_submitted: {
    render: renderSickReportSubmitted,
  },
  sick_report_closed: {
    render: renderSickReportClosed,
  },
  sick_note_added: {
    render: renderSickNoteAdded,
  },
  absence_correction_decided: {
    render: renderAbsenceCorrectionDecided,
  },
  absence_reminder: {
    render: renderAbsenceReminder,
  },
  company_onboarding_step: {
    render: renderCompanyOnboardingStep,
  },
}

// Keyed by `${kind}${absenceType}`, the same request, change and deletion
// wording the server writes into the stored German.
const LEAVE_REQUEST_SUBMITTED_TITLES: Record<string, string> = {
  requestvacation:
    'frontend:notifications.messages.leaveRequestSubmitted.requestVacation',
  requestunpaidLeave:
    'frontend:notifications.messages.leaveRequestSubmitted.requestUnpaidLeave',
  changevacation:
    'frontend:notifications.messages.leaveRequestSubmitted.changeVacation',
  changeunpaidLeave:
    'frontend:notifications.messages.leaveRequestSubmitted.changeUnpaidLeave',
  deletionvacation:
    'frontend:notifications.messages.leaveRequestSubmitted.deletionVacation',
  deletionunpaidLeave:
    'frontend:notifications.messages.leaveRequestSubmitted.deletionUnpaidLeave',
}
const LEAVE_REQUEST_DECIDED_SUBJECTS: Record<string, string> = {
  requestvacation:
    'frontend:notifications.messages.leaveRequestDecided.subject.requestVacation',
  requestunpaidLeave:
    'frontend:notifications.messages.leaveRequestDecided.subject.requestUnpaidLeave',
  changevacation:
    'frontend:notifications.messages.leaveRequestDecided.subject.changeVacation',
  changeunpaidLeave:
    'frontend:notifications.messages.leaveRequestDecided.subject.changeUnpaidLeave',
  deletionvacation:
    'frontend:notifications.messages.leaveRequestDecided.subject.deletionVacation',
  deletionunpaidLeave:
    'frontend:notifications.messages.leaveRequestDecided.subject.deletionUnpaidLeave',
}
const LEAVE_REQUEST_DECISIONS: Record<string, string> = {
  approved: 'frontend:notifications.messages.leaveRequestDecided.approved',
  declined: 'frontend:notifications.messages.leaveRequestDecided.declined',
  withdrawn: 'frontend:notifications.messages.leaveRequestDecided.withdrawn',
  expired: 'frontend:notifications.messages.leaveRequestDecided.expired',
  // Approved with another period than the one asked for.
  approvedWithChanges:
    'frontend:notifications.messages.leaveRequestDecided.approvedWithChanges',
  changeapprovedWithChanges:
    'frontend:notifications.messages.leaveRequestDecided.changeApprovedWithChanges',
}

function leaveRequestDecision(n: NotificationLike): string {
  const decision = asString(n.details?.decision) ?? ''
  if (decision !== 'approved' || n.details?.changed !== true) return decision
  return n.details?.kind === 'change'
    ? 'changeapprovedWithChanges'
    : 'approvedWithChanges'
}

/** A request without a `kind` is a new request, as the server stores it. */
function leaveRequestVariant(n: NotificationLike): string {
  return `${asString(n.details?.kind) ?? 'request'}${asString(n.details?.absenceType)}`
}

function leaveRequestPeriod(n: NotificationLike, t: TFunction): string | null {
  const start = formatDate(n.details?.absenceStart)
  const end = formatDate(n.details?.absenceEnd)
  if (!start || !end) return null
  return start === end
    ? start
    : t('frontend:notifications.messages.leaveRequest.period', { start, end })
}

function renderLeaveRequestSubmitted(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const titleKey = LEAVE_REQUEST_SUBMITTED_TITLES[leaveRequestVariant(n)]
  const employeeName = asString(n.details?.employeeName)
  const period = leaveRequestPeriod(n, t)
  if (!titleKey || !employeeName || !period) return null
  return { title: t(titleKey, { employeeName }), body: period }
}

function renderLeaveRequestDecided(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const subjectKey = LEAVE_REQUEST_DECIDED_SUBJECTS[leaveRequestVariant(n)]
  const decisionKey = LEAVE_REQUEST_DECISIONS[leaveRequestDecision(n)]
  const period = leaveRequestPeriod(n, t)
  if (!subjectKey || !decisionKey || !period) return null
  const reason = asString(n.details?.reason)
  return {
    title: t(decisionKey, { subject: t(subjectKey) }),
    body: reason
      ? `${period} · ${t('frontend:notifications.messages.leaveRequest.reason', { reason })}`
      : period,
  }
}

const SICK_REPORT_TYPES: Record<string, string> = {
  illness: 'frontend:notifications.messages.sickReport.type.illness',
  illnessChild: 'frontend:notifications.messages.sickReport.type.illnessChild',
}

/** Notifications stored before the type was kept name a plain illness. */
function sickReportType(n: NotificationLike, t: TFunction): string {
  return t(
    SICK_REPORT_TYPES[asString(n.details?.absenceType) ?? ''] ??
      SICK_REPORT_TYPES.illness
  )
}

const SICK_REPORT_SUBMITTED_TITLES: Record<string, string> = {
  request: 'frontend:notifications.messages.sickReport.submitted',
  change: 'frontend:notifications.messages.sickReport.submittedChange',
  deletion: 'frontend:notifications.messages.sickReport.submittedDeletion',
}

function renderSickReportSubmitted(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const titleKey =
    SICK_REPORT_SUBMITTED_TITLES[asString(n.details?.kind) ?? 'request']
  const employeeName = asString(n.details?.employeeName)
  const period = leaveRequestPeriod(n, t)
  if (!titleKey || !employeeName || !period) return null
  return {
    title: t(titleKey, { employeeName, sickness: sickReportType(n, t) }),
    body: period,
  }
}

// Keyed by `${kind}${outcome}`; `completed` is how an approval was stored
// before.
const SICK_REPORT_CLOSED_TITLES: Record<string, string> = {
  requestapproved: 'frontend:notifications.messages.sickReport.approved',
  requestcompleted: 'frontend:notifications.messages.sickReport.approved',
  requestdeclined: 'frontend:notifications.messages.sickReport.declined',
  requestdeleted: 'frontend:notifications.messages.sickReport.deleted',
  changeapproved: 'frontend:notifications.messages.sickReport.changeApproved',
  changedeclined: 'frontend:notifications.messages.sickReport.changeDeclined',
  changedeleted: 'frontend:notifications.messages.sickReport.changeDeleted',
  deletionapproved:
    'frontend:notifications.messages.sickReport.deletionApproved',
  deletiondeclined:
    'frontend:notifications.messages.sickReport.deletionDeclined',
}

function renderSickReportClosed(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const titleKey =
    SICK_REPORT_CLOSED_TITLES[
      `${asString(n.details?.kind) ?? 'request'}${asString(n.details?.outcome)}`
    ]
  const period = leaveRequestPeriod(n, t)
  if (!titleKey || !period) return null
  const reason = asString(n.details?.reason)
  return {
    title: t(titleKey, { sickness: sickReportType(n, t) }),
    body: reason
      ? `${period} · ${t('frontend:notifications.messages.leaveRequest.reason', { reason })}`
      : period,
  }
}

function renderSickNoteAdded(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const employeeName = asString(n.details?.employeeName)
  const period = leaveRequestPeriod(n, t)
  if (!employeeName || !period) return null
  return {
    title: t('frontend:notifications.messages.sickReport.noteAdded', {
      employeeName,
    }),
    body: period,
  }
}

const ABSENCE_CORRECTION_DECISIONS: Record<string, string> = {
  applied: 'frontend:notifications.messages.absenceCorrection.applied',
  declined: 'frontend:notifications.messages.absenceCorrection.declined',
}

function renderAbsenceCorrectionDecided(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const titleKey =
    ABSENCE_CORRECTION_DECISIONS[asString(n.details?.decision) ?? '']
  const employeeName = asString(n.details?.employeeName)
  const period = leaveRequestPeriod(n, t)
  if (!titleKey || !employeeName || !period) return null
  const reason = asString(n.details?.reason)
  return {
    title: t(titleKey, { employeeName }),
    body: reason
      ? `${period} · ${t('frontend:notifications.messages.leaveRequest.reason', { reason })}`
      : period,
  }
}

const REMINDER = 'frontend:notifications.messages.absenceReminder'

/** The reminder engine's reminders, by the type in their details. */
function renderAbsenceReminder(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const details = n.details ?? {}
  const count = asNumber(details.count)
  switch (details.reminderType) {
    case 'openRequests':
      if (count === undefined) return null
      return {
        title: t(`${REMINDER}.openRequests.title`),
        body: t(`${REMINDER}.openRequests.body`, { count }),
      }
    case 'monthClose': {
      const deadline = formatDate(details.payrollDeadline)
      const month = formatMonth(details.yearMonth)
      const openRequests = asNumber(details.openRequests)
      if (!deadline || !month || openRequests === undefined) return null
      const lines = [
        t(`${REMINDER}.monthClose.openRequests`, { value: openRequests }),
      ]
      const hourly = asNumber(details.hourlyWithoutHours)
      if (hourly !== undefined) {
        lines.push(
          t(`${REMINDER}.monthClose.hourlyWithoutHours`, { value: hourly })
        )
      }
      const checks = asNumber(details.openChecks)
      if (checks !== undefined) {
        lines.push(t(`${REMINDER}.monthClose.openChecks`, { value: checks }))
      }
      return {
        title: t(`${REMINDER}.monthClose.title`, { deadline }),
        body: `${t(`${REMINDER}.monthClose.body`, { month })} ${lines.join(' · ')}`,
      }
    }
    case 'missingHours': {
      const month = formatMonth(details.yearMonth)
      if (count === undefined || !month) return null
      return {
        title: t(`${REMINDER}.missingHours.title`),
        body: t(`${REMINDER}.missingHours.body`, { count, month }),
      }
    }
    case 'remainingVacation': {
      const days = asNumber(details.days)
      const year = asNumber(details.year)
      if (days === undefined || year === undefined) return null
      return {
        title: t(`${REMINDER}.remainingVacation.title`),
        body: t(`${REMINDER}.remainingVacation.body`, {
          year,
          days: t(`${REMINDER}.remainingVacation.days`, { count: days }),
        }),
      }
    }
    case 'carryOverNotice': {
      const year = asNumber(details.year)
      if (count === undefined || year === undefined) return null
      return {
        title: t(`${REMINDER}.carryOverNotice.title`, {
          previousYear: year - 1,
        }),
        body: t(`${REMINDER}.carryOverNotice.body`, { count, year }),
      }
    }
    default:
      return null
  }
}

// Sync-issue body is a " | "-joined list grouped by skip reason, mirroring the
// server build in apps/personio.ts. The no_compensations label stays German
// (contains glossary wage terms Festgehalt/Stundenlohn). Order matches the server.
const SYNC_ISSUE_REASON_KEYS: Record<string, string> = {
  no_compensations:
    'frontend:notifications.messages.personioSyncIssue.reason.no_compensations',
  no_employments:
    'frontend:notifications.messages.personioSyncIssue.reason.no_employments',
  non_internal_employment:
    'frontend:notifications.messages.personioSyncIssue.reason.non_internal_employment',
  no_employee_number:
    'frontend:notifications.messages.personioSyncIssue.reason.no_employee_number',
}
const SYNC_ISSUE_REASON_ORDER = [
  'no_compensations',
  'no_employments',
  'non_internal_employment',
  'no_employee_number',
]

function renderPersonioSyncIssue(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const skipped = n.details?.skippedEmployees
  if (!Array.isArray(skipped) || skipped.length === 0) return null

  const parts: string[] = []
  for (const reason of SYNC_ISSUE_REASON_ORDER) {
    const group = skipped.filter((e) => e && e.reason === reason)
    if (group.length === 0) continue
    const shown = group.slice(0, 3)
    // Guard the interpolated fields: a malformed entry would otherwise render
    // a literal "undefined". If any displayed entry is incomplete, fall back to
    // the stored German rather than show a broken fragment.
    const allComplete = shown.every(
      (e) =>
        e.firstName != null &&
        e.lastName != null &&
        (reason === 'no_employee_number'
          ? e.personioId != null
          : e.employeeNumber != null)
    )
    if (!allComplete) return null
    const names = shown
      .map((e) =>
        reason === 'no_employee_number'
          ? `${e.firstName} ${e.lastName} (Personio #${e.personioId})`
          : `${e.firstName} ${e.lastName} (MA${e.employeeNumber})`
      )
      .join(', ')
    const more = group.length > 3 ? ` +${group.length - 3}` : ''
    parts.push(`${t(SYNC_ISSUE_REASON_KEYS[reason])}: ${names}${more}`)
  }
  if (parts.length === 0) return null

  return {
    title: t('frontend:notifications.messages.personioSyncIssue.title', {
      count: skipped.length,
    }),
    body: parts.join(' | '),
  }
}

function renderCompanyDataChanged(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const changedByName = asString(n.details?.changedByName)
  const companyName = n.contextName
  const fields = localizedFieldLabels(
    n.details?.changedFields,
    'frontend:notifications.messages.companyDataChanged.fields',
    t
  )
  if (!changedByName || !companyName || !fields) return null

  return {
    title: t('frontend:notifications.messages.companyDataChanged.title'),
    body: t('frontend:notifications.messages.companyDataChanged.body', {
      changedByName,
      companyName,
      fields,
    }),
  }
}

function renderEmployeePersonalDataChanged(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const names = asStringArray(n.details?.employeeNames)
  if (!names || names.length === 0) return null

  const count = names.length
  let body: string
  if (count === 1) {
    const fields = localizedFieldLabels(
      n.details?.changedFields,
      'frontend:notifications.messages.employeePersonalDataChanged.fields',
      t
    )
    if (!fields) return null
    body = t(
      'frontend:notifications.messages.employeePersonalDataChanged.body',
      { count, employeeName: names[0], fields }
    )
  } else {
    body = t(
      'frontend:notifications.messages.employeePersonalDataChanged.body',
      { count, employeeNames: names.join(', ') }
    )
  }

  if (n.details?.healthInsuranceChanged === true) {
    body += ` ${t('frontend:notifications.messages.employeePersonalDataChanged.sepaHint')}`
  }

  return {
    title: t(
      'frontend:notifications.messages.employeePersonalDataChanged.title',
      { count, employeeName: names[0] }
    ),
    body,
  }
}

const COMPANY_ONBOARDING_STEPS: Record<
  string,
  { title: string; body: string }
> = {
  data_submitted: {
    title:
      'frontend:notifications.messages.companyOnboardingStep.dataSubmitted.title',
    body: 'frontend:notifications.messages.companyOnboardingStep.dataSubmitted.body',
  },
  data_sent_back: {
    title:
      'frontend:notifications.messages.companyOnboardingStep.dataSentBack.title',
    body: 'frontend:notifications.messages.companyOnboardingStep.dataSentBack.body',
  },
  data_accepted: {
    title:
      'frontend:notifications.messages.companyOnboardingStep.dataAccepted.title',
    body: 'frontend:notifications.messages.companyOnboardingStep.dataAccepted.body',
  },
  setup_completed: {
    title:
      'frontend:notifications.messages.companyOnboardingStep.setupCompleted.title',
    body: 'frontend:notifications.messages.companyOnboardingStep.setupCompleted.body',
  },
  onboarding_completed: {
    title:
      'frontend:notifications.messages.companyOnboardingStep.onboardingCompleted.title',
    body: 'frontend:notifications.messages.companyOnboardingStep.onboardingCompleted.body',
  },
  data_reminder: {
    title:
      'frontend:notifications.messages.companyOnboardingStep.dataReminder.title',
    body: 'frontend:notifications.messages.companyOnboardingStep.dataReminder.body',
  },
}

function renderCompanyOnboardingStep(
  n: NotificationLike,
  t: TFunction
): { title: string; body: string } | null {
  const step = asString(n.details?.step) ?? ''
  const keys = COMPANY_ONBOARDING_STEPS[step]
  if (!keys) return null
  const message = asString(n.details?.message)
  const startMonth = asString(n.details?.startMonth)
  if (step === 'data_reminder' && !startMonth) return null
  const body = t(keys.body, {
    month: startMonth ? dayjs(startMonth).format('MMMM YYYY') : undefined,
  })
  return {
    title: t(keys.title, { companyName: n.contextName }),
    body: !message
      ? body
      : step === 'data_sent_back'
        ? message
        : `${body} · ${message}`,
  }
}

/**
 * Resolve the localized `{ title, body }` for a notification, falling back to the
 * stored German strings when the type is unmapped or a required value is missing.
 * `t` is the i18next translate function (from `useTranslation()` or `i18n.t`).
 */
export function getLocalizedNotificationText(
  notification: NotificationLike,
  t: TFunction
): { title: string; body: string } {
  const fallback = { title: notification.title, body: notification.body }
  const cfg = NOTIFICATION_MESSAGES[notification.type]
  if (!cfg) return fallback

  // Assembled-body messages (e.g. Personio sync issue) render themselves.
  if (cfg.render) return cfg.render(notification, t) ?? fallback
  if (!cfg.title || !cfg.body) return fallback

  const params: InterpolationParams = cfg.params ? cfg.params(notification) : {}
  const context = cfg.context ? cfg.context(notification) : undefined

  const required =
    typeof cfg.required === 'function'
      ? cfg.required(notification, context)
      : (cfg.required ?? [])
  if (
    required.some((key) => params[key] === undefined || params[key] === null)
  ) {
    return fallback
  }

  const options: InterpolationParams = { ...params }
  if (context) options.context = context

  return { title: t(cfg.title, options), body: t(cfg.body, options) }
}
